import React, { createContext, useContext } from "react"

import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetModalProvider,
} from "@gorhom/bottom-sheet"
import { useMMKVString } from "react-native-mmkv"

import { SentimentSelect } from "src/components/SentimentSelect"
import { useAppTheme } from "src/hooks/useAppTheme"

export enum Sentiment {
  DISSATISFIED = "dissatisfied",
  NEUTRAL = "neutral",
  SATISFIED = "satisfied",
}

export enum SentimentValue {
  DISSATISFIED = 1,
  NEUTRAL = 2,
  SATISFIED = 3,
}
export enum SentimentString {
  DISSATISFIED = "sad",
  NEUTRAL = "neutral",
  SATISFIED = "happy",
}

export const SentimentIcon = (sentiment: Sentiment) => {
  switch (sentiment) {
    case Sentiment.DISSATISFIED:
      return "sentiment-dissatisfied"
    case Sentiment.NEUTRAL:
      return "sentiment-neutral"
    case Sentiment.SATISFIED:
      return "sentiment-satisfied-alt"
    default:
      return "sentiment-neutral"
  }
}

type SentimentModalContextType = {
  sentimentModalRef: React.RefObject<BottomSheetModal | null>
  currentSentiment: Sentiment | undefined
  sentimentText: string | undefined
  setPendingSentiment: (value: Sentiment) => void
  commitSentiment: () => void
  pendingSentiment: Sentiment | undefined
}

const SentimentModalContext = createContext<SentimentModalContextType | undefined>(undefined)

export const SentimentModalProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { theme } = useAppTheme()
  const sentimentModalRef = React.useRef<BottomSheetModal>(null)

  const [currentSentiment, _setCurrentSentiment] = useMMKVString("currentSentiment")
  const [pendingSentiment, setPendingSentiment] = React.useState<Sentiment | undefined>()

  const sentimentText = React.useMemo(() => {
    switch (pendingSentiment || currentSentiment) {
      case Sentiment.DISSATISFIED:
        return "sad"
      case Sentiment.NEUTRAL:
        return "neutral"
      case Sentiment.SATISFIED:
        return "happy"
      default:
        return undefined
    }
  }, [pendingSentiment, currentSentiment])

  const commitSentiment = React.useCallback(() => {
    if (pendingSentiment) {
      _setCurrentSentiment(pendingSentiment)
      setPendingSentiment(undefined)
    }
  }, [pendingSentiment, _setCurrentSentiment])

  const value = {
    currentSentiment: currentSentiment as Sentiment | undefined,
    sentimentText,
    pendingSentiment,
    setPendingSentiment,
    commitSentiment,
    sentimentModalRef,
  }

  return (
    <SentimentModalContext.Provider value={value}>
      <BottomSheetModalProvider>
        {children}

        <BottomSheetModal
          ref={sentimentModalRef}
          snapPoints={["25%"]}
          backgroundStyle={{ backgroundColor: theme.colors.background }}
          backdropComponent={(props) => (
            <BottomSheetBackdrop
              {...props}
              appearsOnIndex={0}
              disappearsOnIndex={-1}
              onPress={() => {
                sentimentModalRef.current?.close()
              }}
            />
          )}
        >
          <SentimentSelect
            onSentimentChange={(sentiment) => {
              // Set the pending sentiment so that the chat context can watch for the change
              // and send the sentiment to the server.
              setPendingSentiment(sentiment)
              // we don't actually need to do anything with the sentiment change right now
              // we just need to close the modal
              sentimentModalRef.current?.close()
            }}
          />
        </BottomSheetModal>
      </BottomSheetModalProvider>
    </SentimentModalContext.Provider>
  )
}

export const useSentimentModal = () => {
  const context = useContext(SentimentModalContext)
  if (context === undefined) {
    throw new Error("useSentimentModal must be used within a SentimentModalProvider")
  }
  return context
}
